import type { ILead } from 'src/services/api';
import { formatSmartField } from 'src/components/lead-sidebar/format-smart-field';

export type DisplayedSmartField = {
    key: string;
    label: string;
    value: string;
};

/* Lead facts now live on the lead's own columns (shown via the contact rows and
   LeadFactsList), so their legacy smart_fields_data copies are excluded here to avoid
   showing the same fact twice. */
const FACT_KEYS = new Set([
    'monthly_income',
    'annual_income',
    'household_size',
    'pet_type',
    'has_pet',
    'voucher_type',
    'phone_number',
    'email',
]);

/* Server-managed / internal keys the client never sets and shouldn't display. */
const isSystemKey = (key: string): boolean =>
    key.startsWith('_') ||
    key.startsWith('llm_calls') ||
    key.endsWith('_suggestion_status') ||
    key.endsWith('_suggested_value');

/**
 * The smart_fields_data entries shown in Lead Info (after the lead facts): string
 * values that are neither lead facts (now columns) nor internal keys, each labelled via
 * {@link formatSmartField} and sorted by label.
 */
export function getDisplayedSmartFields(lead: Pick<ILead, 'smart_fields_data'>): DisplayedSmartField[] {
    const smartFields = lead.smart_fields_data ?? {};
    return Object.entries(smartFields)
        .filter(
            (entry): entry is [string, string] =>
                typeof entry[1] === 'string' && !FACT_KEYS.has(entry[0]) && !isSystemKey(entry[0])
        )
        .map(([key, value]) => ({ key, label: formatSmartField(key, value).fieldLabel, value }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
